import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApartments, useDeleteApartment } from "@/api/districts";
import { ApartmentDetailSheet } from "@/components/districts/apartment-detail-sheet";
import { CreateApartmentDialog } from "@/components/districts/create-apartment-dialog";
import { DeleteApartmentAlertDialog } from "@/components/districts/delete-apartment-alert-dialog";
import { QueryError } from "@/components/shared/query-error";
import { DataTablePagination } from "@/components/table/data-table-pagination";
import { useTableUrlState } from "@/components/table/use-table-url-state";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { type SortField, SortIcon } from "./district-apartments-sort";
import { ApartmentRows, SkeletonRows } from "./district-apartments-table-body";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

interface DistrictApartmentsTableProps {
	districtId: number;
	total?: number;
}

export function DistrictApartmentsTable({
	districtId,
	total,
}: DistrictApartmentsTableProps) {
	const { t } = useTranslation();
	const hideElectricity = useHideElectricity();
	const [createOpen, setCreateOpen] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
	const deleteApartment = useDeleteApartment();

	const {
		page,
		pageSize,
		search,
		ordering,
		setPage,
		setPageSize,
		setSearch,
		setOrdering,
	} = useTableUrlState();
	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isError, refetch } = useApartments({
		district_id: districtId,
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	});

	const sortField = (ordering.replace(/^-/, "") as SortField) || null;
	const sortDir = ordering.startsWith("-") ? "desc" : "asc";

	function handleSort(field: SortField) {
		if (sortField === field) {
			if (sortDir === "asc") setOrdering(`-${field}`);
			else {
				setOrdering("");
			}
		} else {
			setOrdering(field);
		}
		setPage(1);
	}

	const totalCount = data?.count ?? total ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3">
			<div className="flex items-center justify-between gap-2">
				<SearchInput
					className="w-56"
					placeholder={t("apartments.searchPlaceholder")}
					value={search}
					onValueChange={setSearch}
				/>
				<Button onClick={() => setCreateOpen(true)}>
					<PlusIcon />
					{t("apartments.createButton")}
				</Button>
			</div>

			<div className="min-h-0 flex-1 overflow-auto rounded-md border [&_[data-slot=table-container]]:overflow-x-visible">
				<Table>
					<TableHeader className="sticky top-0 z-10">
						<TableRow>
							<TableHead
								className="cursor-pointer select-none"
								onClick={() => handleSort("street")}
							>
								<span className="flex items-center gap-1.5">
									{t("apartments.colStreet")}
									<SortIcon
										field="street"
										sortField={sortField}
										sortDir={sortDir}
									/>
								</span>
							</TableHead>
							<TableHead>{t("apartments.colNumber")}</TableHead>
							<TableHead>{t("apartments.colColdWater")}</TableHead>
							<TableHead>{t("apartments.colHotWater")}</TableHead>
							<TableHead>{t("apartments.colHeat")}</TableHead>
							{!hideElectricity && (
								<TableHead>{t("apartments.colElectricity")}</TableHead>
							)}
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<SkeletonRows />
						) : isError ? (
							<TableRow>
								<TableCell colSpan={hideElectricity ? 6 : 7} className="py-8">
									<QueryError onRetry={() => refetch()} />
								</TableCell>
							</TableRow>
						) : (
							<ApartmentRows
								apartments={data?.results ?? []}
								onSelect={setSelectedId}
								search={search}
								hideElectricity={hideElectricity}
							/>
						)}
					</TableBody>
				</Table>
			</div>

			<DataTablePagination
				page={page}
				pageSize={pageSize}
				count={totalCount}
				onPageChange={setPage}
				onPageSizeChange={(v) => setPageSize(v)}
				pageSizeOptions={PAGE_SIZE_OPTIONS as unknown as number[]}
				hasPrevious={page > 1}
				hasNext={page < totalPages}
			/>

			<CreateApartmentDialog
				open={createOpen}
				onOpenChange={(o) => !o && setCreateOpen(false)}
				districtId={districtId}
			/>
			<ApartmentDetailSheet
				apartmentId={selectedId}
				onOpenChange={(o) => !o && setSelectedId(null)}
				onRequestDelete={(id) => {
					setSelectedId(null);
					setPendingDeleteId(id);
				}}
			/>
			<DeleteApartmentAlertDialog
				open={pendingDeleteId !== null}
				onOpenChange={(o) => !o && setPendingDeleteId(null)}
				pending={deleteApartment.isPending}
				onConfirm={() => {
					if (pendingDeleteId !== null) {
						deleteApartment.mutate(pendingDeleteId, {
							onSuccess: () => {
								toast.success(t("apartments.deleted"));
								setPendingDeleteId(null);
							},
							onError: () => {
								toast.error(t("apartments.deleteError"));
							},
						});
					}
				}}
			/>
		</div>
	);
}
